import {
  PAID_TASK_STATUS_APPROVED,
  PAID_TASK_STATUS_ASSIGNED,
  PAID_TASK_STATUS_CREATED,
  PAID_TASK_STATUS_IN_REVIEW,
  PAID_TASK_STATUS_REQUESTED,
} from "../components/PaidTask/paidTaskStatus";
import { STAGE_ACTION_REQUIRED, STAGE_CHECKED, STAGE_LAWSUIT, STAGE_PREPARE_LAWSUIT } from "./Stages/StagesCase";
import { intFieldDefault, maxNumber, requiredFieldDefault as requiredFieldDefaultLegacy } from "./validationRules";
import _ from "lodash";
import { userHasOneOfTheseRoles, userHasRole } from "./backofficeUserService";
import { BackofficeUser, Roles } from "../types/BackofficeUser";
import { dateFieldDefaultFieldBased, requiredFieldDefaultFieldBased } from "./reactHookFormValidationRules";
import { ExternalLawyerCapacity } from "../types/ExternalLawyerCapacity";
import { PaidTask } from "../types/PaidTask";
import { PaidTaskTemplate } from "../types/PaidTaskTemplate";
import { UseForm } from "../types/UseForm";
import ApiClient from "./ApiClient";
import { ProductUnion } from "../types/ProductUnion";
import { STAGE_ADMINISTRATIVE_DECISION_ISSUED } from "./Stages/StagesCivilServiceLaw";
import dayjs from "dayjs";

const externalLawyerTemplateLink = "https://drive.google.com/drive/u/0/folders/176VCWhO4XbyHn5qr40bszZZoCTTHgPlk";

const genericIterableAttachmentConfig = {
  identifier: "{{iterator}}",
  headline: "Weitere Datei",
  description: " Upload einer weiteren zur Bearbeitung notwendigen Datei. Dateiformat: PDF - Maximalgröße: 10 MB",
  fileName: "{{YYYYMMDD}}_ergaenzende_datei_{{iterator}}.pdf",
  fileDescription: "Ergänzende Datei {{iterator}} zu Schriftsatz",
};

const litigationCaseTemplates: PaidTaskTemplate[] = [
  {
    type: "statementOfClaim",
    label: "Klageschrift",
    subject: "Klageschrift verfassen",
    description:
      "Im vorliegenden Fall soll eine Klageschrift an das zuständige Gericht erstellt werden." +
      " Das Klageziel ergibt sich aus dieser Aufgabe, dem außergerichtlichen Schreiben (s. Dokumente) sowie ggf. der Notiz am Fall." +
      " Bei etwaigen Fragen zur Sache selber kann die Mandantschaft angerufen werden. Der Anruf ist per Notiz zu dokumentieren.",
    definitionOfDone:
      "Gerichtsstand ermitteln:\n" +
      " - Der Gerichtsstand ist anhand der Falldaten zu bestimmen und unten im Feld “PLZ Gerichtsstand” einzutragen\n" +
      "\n" +
      "Vorlage:\n" +
      " - Bitte diese Word-Vorlage verwenden: " +
      externalLawyerTemplateLink +
      "\n" +
      "\n" +
      "Folgende Anträge sind zu stellen:\n" +
      " - Hauptsacheantrag inkl. Zinsantrag ab Verzug (ein Tag nach im Aufforderungsschreiben gesetzter Frist, wenn nicht" +
      " ab Rechtshängigkeit): Nacherfüllung\n" +
      " - Anträge 2 - 5 sind aus Vorlage zu übernehmen; Antrag zu 3. muss bei Rücktrittsantrag gestellt werden. " +
      "Ebenso Anregungen über Verfahren (495a ZPO, früher erster Termin)\n" +
      "\n" +
      "Formelle Anforderungen:\n" +
      " - Erstellen des Schriftsatzes als PDF Datei\n" +
      " - Das PDF soll bei Ihnen lokal erzeugt und dann unten hochgeladen werden.\n" +
      " - Anlagen sollen jeweils einzeln erstellt und durchnummeriert werden (K1, K2, etc.). „Anlage K1“ etc. muss oben " +
      "rechts auf Dokument stehen.\n" +
      " - Das Klage-pdf und alle Anlagen als weitere Einzel pdfs sind unten unter “Bearbeitung” hochzuladen\n" +
      " - Besonderheiten oder Informationen zu Rücksprachen mit der Mandantschaft sind per Notizfunktion am Fall vermerkt.\n" +
      " - Sachfragen bitte an Mandantschaft direkt stellen; juristische bzw. Verfahrensfragen gerne an jeweiligen im Fall " +
      "hinterlegten RA. Verbesserungsvorschläge sind jederzeit gerne willkommen!",
    reviewRemarks: "",
    assignmentDeadline: { amount: 21, unit: "days" },
    doneDeadline: { amount: 28, unit: "days" },
    maxFee: "100",
    fee: "60",
    isHidden: ({ product }: { product: ProductUnion }) =>
      !product || product.stage === null || product.stage < STAGE_PREPARE_LAWSUIT || product.stage < STAGE_CHECKED,
    getIsUserRequestable: ({ externalLawyerCapacity }: { externalLawyerCapacity: ExternalLawyerCapacity }) =>
      !isWbsLawyer(externalLawyerCapacity.backofficeUser) && externalLawyerCapacity.isAvailable,
    hasEditor: false,
    mainDocumentConfig: {
      headline: "Klageschrift",
      description: "Bitte laden Sie hier die Klageschrift als PDF-Datei hoch",
      fileName: "{{YYYYMMDD}}_Klage_{{clientFamilyName}}_aG.pdf",
      fileDescription: "Klage",
    },
    iterableAttachmentsConfig: {
      identifier: "K{{iterator}}",
      headline: "Anlage",
      description:
        "Bitte laden Sie hier Anlage K{{iterator}} hoch. Achten Sie darauf, dass in dem Dokument am oberen Bildrand “K{{iterator}}”vermerkt ist.",
      fileName: "{{YYYYMMDD}}_Klage_AnlageK{{iterator}}_{{clientFamilyName}}.pdf",
      fileDescription: "Anlage K{{iterator}} Klage",
    },
    fields: [
      {
        type: "TextField",
        path: `courtPostalCode`,
        validators: requiredFieldDefaultFieldBased,
        headline: "Postleitzahl des Gerichtsstands",
        description:
          "Bitte tragen Sie hier die Postleitzahl ein, die Sie für den Gerichtsstand ermittelt haben. Wichtig: Bitte NICHT die Postleitzahl des Gerichtes eintragen, sondern die des Gerichtsstands.",
      },
      {
        type: "NumberField",
        path: `valueInDisputeJudicialPreliminary`,
        validators: requiredFieldDefaultFieldBased,
        headline: "Vorläufiger Streitwert",
        description:
          "Bitte tragen Sie hier den vorläufigen Streitwert ein, den Sie in der Klageschrift vermerkt haben.",
        fieldType: "money",
        numberType: "float",
      },
    ],
  },
  {
    type: "statementOfDefense",
    label: "Klageerwiderung des Beklagten",
    subject: "Klageerwiderung des Beklagten",
    description:
      "Im vorliegenden Fall soll eine Klageerwiderung erstellt werden. Weiteres ist dem Fall und den dortigen Dateien zu entnehmen.",
    definitionOfDone:
      "- Vorlage: Bitte diese Word-Vorlage verwenden:  " +
      externalLawyerTemplateLink +
      "\n" +
      "- Verarbeiten der Stellungnahme der Mandantschaft zum Schriftsatz der Gegenseite (wenn vorhanden)\n" +
      "- Wo nötig Beweis antreten bzw. Bestreiten/mit Nichtwissen bestreiten\n" +
      "- Als Abschluss des Schriftsatzes bitte folgende beiden Absätze verwenden: Soweit das Gericht entgegen der " +
      "diesseitigen Auffassung den Vortrag als unzureichend, unschlüssig oder unsubstantiiert ansieht, um den geltend " +
      "gemachten Anspruch als schlüssig zu qualifizieren, oder wenn sonst Bedenken gegen die gefassten Anträge, die " +
      "Schlüssigkeit und die Substantiierung des Vortrages und die hiesige Sichtweise in der Beweislast bestehen, wird " +
      "um einen entsprechenden Hinweis nach § 139 ZPO bzw. eine prozessleitende Verfügung nach § 273 Abs. 2 Nr. 1 ZPO gebeten.\n\n" +
      "Der Unterzeichner weist allein aus Gründen der Vorsorge gegenüber seiner Mandantschaft darauf hin, dass die " +
      "gerichtliche Hinweis- und Aufklärungspflicht nach der neueren Rechtsprechung auch gegenüber der anwaltlich " +
      "vertretenden Partei uneingeschränkt besteht (BGH NJW 2001, 2548; OLG Köln NJW-RR 2001, 1724).",
    reviewRemarks: "",
    assignmentDeadline: { amount: 7, unit: "days" },
    doneDeadline: { amount: 14, unit: "days" },
    maxFee: "75",
    fee: "50",
    isHidden: ({ product }: { product: ProductUnion }) =>
      !product || product.stage === null || product.stage < STAGE_LAWSUIT || product.stage < STAGE_CHECKED,
    getIsUserRequestable: ({ externalLawyerCapacity }: { externalLawyerCapacity: ExternalLawyerCapacity }) =>
      !isWbsLawyer(externalLawyerCapacity.backofficeUser) && externalLawyerCapacity.isAvailable,
    hasEditor: false,
    mainDocumentConfig: {
      headline: "Klageerwiderung des Beklagten",
      description: "Bitte laden Sie hier die Klageerwiderung als PDF-Datei hoch",
      fileName: "{{YYYYMMDD}}_KE_Klageerwiderung_{{clientFamilyName}}_aG.pdf",
      fileDescription: "Klageerwiderung",
    },
    iterableAttachmentsConfig: {
      identifier: "B{{iterator}}",
      headline: "Anlage",
      description:
        "Bitte laden Sie hier Beweis B{{iterator}} hoch. Achten Sie darauf, dass in dem Dokument am oberen Bildrand “B{{iterator}}”vermerkt ist.",
      fileName: "{{YYYYMMDD}}_Klageerwiderung_AnlageB{{iterator}}_{{clientFamilyName}}.pdf",
      fileDescription: "Beweis {{iterator}} Klageerwiderung",
    },
  },
  {
    type: "counterplea",
    label: "Replik des Klägers",
    subject: "Replik auf Klageerwiderung",
    description:
      "Im vorliegenden Fall soll eine Replik erstellt werden. Weiteres ist dem Fall und den dortigen Dateien zu entnehmen.",
    definitionOfDone:
      "- Vorlage: Bitte diese Word-Vorlage verwenden: " +
      externalLawyerTemplateLink +
      "\n" +
      "- Verarbeiten der Stellungnahme der Mandantschaft zum Schriftsatz der Gegenseite (wenn vorhanden)\n" +
      "- Wo nötig Beweis antreten bzw. Bestreiten/mit Nichtwissen bestreiten\n" +
      "- Als Abschluss des Schriftsatzes bitte folgende beiden Absätze verwenden: Soweit das Gericht entgegen der" +
      " diesseitigen Auffassung den Vortrag als unzureichend, unschlüssig oder unsubstantiiert ansieht, um den geltend" +
      " gemachten Anspruch als schlüssig zu qualifizieren, oder wenn sonst Bedenken gegen die gefassten Anträge," +
      " die Schlüssigkeit und die Substantiierung des Vortrages und die hiesige Sichtweise in der Beweislast bestehen," +
      " wird um einen entsprechenden Hinweis nach § 139 ZPO bzw. eine prozessleitende Verfügung nach" +
      " § 273 Abs. 2 Nr. 1 ZPO gebeten.\n\n" +
      "Der Unterzeichner weist allein aus Gründen der Vorsorge gegenüber seiner Mandantschaft darauf hin, dass die" +
      " gerichtliche Hinweis- und Aufklärungspflicht nach der neueren Rechtsprechung auch gegenüber der anwaltlich" +
      " vertretenden Partei uneingeschränkt besteht (BGH NJW 2001, 2548; OLG Köln NJW-RR 2001, 1724).",
    reviewRemarks: "",
    assignmentDeadline: { amount: 7, unit: "days" },
    doneDeadline: { amount: 14, unit: "days" },
    maxFee: "75",
    fee: "50",
    isHidden: ({ product }: { product: ProductUnion }) =>
      !product || product.stage === null || product.stage < STAGE_LAWSUIT || product.stage < STAGE_CHECKED,
    getIsUserRequestable: ({ externalLawyerCapacity }: { externalLawyerCapacity: ExternalLawyerCapacity }) =>
      !isWbsLawyer(externalLawyerCapacity.backofficeUser) && externalLawyerCapacity.isAvailable,
    hasEditor: false,
    mainDocumentConfig: {
      headline: "Replik des Klägers",
      description: "Bitte laden Sie hier die Replik als PDF-Datei hoch",
      fileName: "{{YYYYMMDD}}_REP_Replik_{{clientFamilyName}}_aG.pdf",
      fileDescription: "Replik",
    },
    iterableAttachmentsConfig: {
      identifier: "K{{iterator}}",
      headline: "Anlage",
      description:
        "Bitte laden Sie hier Anlage K{{iterator}} hoch. Achten Sie darauf, dass in dem Dokument am oberen Bildrand “K{{iterator}}”vermerkt ist.",
      fileName: "{{YYYYMMDD}}_Replik_AnlageK{{iterator}}_{{clientFamilyName}}.pdf",
      fileDescription: "Anlage {{iterator}} Replik",
    },
  },
  {
    type: "rejoinder",
    label: "Duplik des Beklagten",
    subject: "Duplik des Beklagten",
    description:
      "Im vorliegenden Fall soll eine Duplik als Reaktion auf das Schreiben der Gegenseite erstellt werden. Weiteres ist dem Fall und den dortigen Dateien zu entnehmen.",
    definitionOfDone:
      "- Vorlage: Bitte diese Word-Vorlage verwenden: " +
      externalLawyerTemplateLink +
      "\n" +
      "- Verarbeiten der Stellungnahme der Mandantschaft zum Schriftsatz der Gegenseite (wenn vorhanden)\n" +
      "- Wo nötig Beweis antreten bzw. Bestreiten/mit Nichtwissen bestreiten\n" +
      "- Als Abschluss des Schriftsatzes bitte folgende beiden Absätze verwenden: Soweit das Gericht entgegen der" +
      " diesseitigen Auffassung den Vortrag als unzureichend, unschlüssig oder unsubstantiiert ansieht, um den geltend" +
      " gemachten Anspruch als schlüssig zu qualifizieren, oder wenn sonst Bedenken gegen die gefassten Anträge," +
      " die Schlüssigkeit und die Substantiierung des Vortrages und die hiesige Sichtweise in der Beweislast bestehen," +
      " wird um einen entsprechenden Hinweis nach § 139 ZPO bzw. eine prozessleitende Verfügung nach" +
      " § 273 Abs. 2 Nr. 1 ZPO gebeten.\n\n" +
      "Der Unterzeichner weist allein aus Gründen der Vorsorge gegenüber seiner Mandantschaft darauf hin, dass die" +
      " gerichtliche Hinweis- und Aufklärungspflicht nach der neueren Rechtsprechung auch gegenüber der anwaltlich" +
      " vertretenden Partei uneingeschränkt besteht (BGH NJW 2001, 2548; OLG Köln NJW-RR 2001, 1724).",
    reviewRemarks: "",
    assignmentDeadline: { amount: 7, unit: "days" },
    doneDeadline: { amount: 14, unit: "days" },
    maxFee: "75",
    fee: "50",
    isHidden: ({ product }: { product: ProductUnion }) =>
      !product || product.stage === null || product.stage < STAGE_LAWSUIT || product.stage < STAGE_CHECKED,
    getIsUserRequestable: ({ externalLawyerCapacity }: { externalLawyerCapacity: ExternalLawyerCapacity }) =>
      !isWbsLawyer(externalLawyerCapacity.backofficeUser) && externalLawyerCapacity.isAvailable,
    hasEditor: false,
    mainDocumentConfig: {
      headline: "Duplik des Beklagten",
      description: "Bitte laden Sie hier die Duplik als PDF-Datei hoch",
      fileName: "{{YYYYMMDD}}_REP_Duplik_{{clientFamilyName}}_aG.pdf",
      fileDescription: "Duplik",
    },
    iterableAttachmentsConfig: {
      identifier: "B{{iterator}}",
      headline: "Anlage",
      description:
        "Bitte laden Sie hier Beweis B{{iterator}} hoch. Achten Sie darauf, dass in dem Dokument am oberen Bildrand “B{{iterator}}”vermerkt ist.",
      fileName: "{{YYYYMMDD}}_Duplik_AnlageB{{iterator}}_{{clientFamilyName}}.pdf",
      fileDescription: "Beweis {{iterator}} Duplik",
    },
  },
  {
    type: "surrejoinder",
    label: "Triplik des Klägers",
    subject: "Triplik des Klägers",
    description:
      "Im vorliegenden Fall soll eine Triplik erstellt werden. Weiteres ist dem Fall und den dortigen Dateien zu entnehmen.",
    definitionOfDone:
      "- Vorlage: Bitte diese Word-Vorlage verwenden: " +
      externalLawyerTemplateLink +
      "\n" +
      "- Verarbeiten der Stellungnahme der Mandantschaft zum Schriftsatz der Gegenseite (wenn vorhanden)\n" +
      "- Wo nötig Beweis antreten bzw. Bestreiten/mit Nichtwissen bestreiten\n" +
      "- Als Abschluss des Schriftsatzes bitte folgende beiden Absätze verwenden: Soweit das Gericht entgegen der " +
      "diesseitigen Auffassung den Vortrag als unzureichend, unschlüssig oder unsubstantiiert ansieht, um den geltend " +
      "gemachten Anspruch als schlüssig zu qualifizieren, oder wenn sonst Bedenken gegen die gefassten Anträge, die " +
      "Schlüssigkeit und die Substantiierung des Vortrages und die hiesige Sichtweise in der Beweislast bestehen, wird " +
      "um einen entsprechenden Hinweis nach § 139 ZPO bzw. eine prozessleitende Verfügung nach § 273 Abs. 2 Nr. 1 ZPO gebeten.\n\n" +
      "Der Unterzeichner weist allein aus Gründen der Vorsorge gegenüber seiner Mandantschaft darauf hin, dass die gerichtliche" +
      " Hinweis- und Aufklärungspflicht nach der neueren Rechtsprechung auch gegenüber der anwaltlich vertretenden Partei " +
      "uneingeschränkt besteht (BGH NJW 2001, 2548; OLG Köln NJW-RR 2001, 1724).",
    reviewRemarks: "",
    assignmentDeadline: { amount: 7, unit: "days" },
    doneDeadline: { amount: 14, unit: "days" },
    maxFee: "75",
    fee: "50",
    isHidden: ({ product }: { product: ProductUnion }) =>
      !product || product.stage === null || product.stage < STAGE_LAWSUIT || product.stage < STAGE_CHECKED,
    getIsUserRequestable: ({ externalLawyerCapacity }: { externalLawyerCapacity: ExternalLawyerCapacity }) =>
      !isWbsLawyer(externalLawyerCapacity.backofficeUser) && externalLawyerCapacity.isAvailable,
    hasEditor: false,
    mainDocumentConfig: {
      headline: "Triplik des Klägers",
      description: "Bitte laden Sie hier die Triplik des Klägers als PDF-Datei hoch",
      fileName: "{{YYYYMMDD}}_Triplik_{{clientFamilyName}}_aG.pdf",
      fileDescription: "Triplik",
    },
    iterableAttachmentsConfig: {
      identifier: "K{{iterator}}",
      headline: "Anlage",
      description:
        " Bitte fügen Sie hier Anlage K{{iterator}} als JPG, PNG oder PDF hoch. Achten Sie darauf, dass in dem Dokument am oberen Bildrand “K{{iterator}}”vermerkt ist.",
      fileName: "{{YYYYMMDD}}_Triplik_AnlageK{{iterator}}_{{clientFamilyName}}.pdf",
      fileDescription: "Anlage {{iterator}} Triplik",
    },
  },
  {
    type: "rebuttal",
    label: "Quadruplik des Beklagten",
    subject: "Quadruplik des Beklagten",
    description:
      "Im vorliegenden Fall soll eine Quadruplik erstellt werden. Weiteres ist dem Fall und den dortigen Dateien zu entnehmen.",
    definitionOfDone:
      "- Vorlage: Bitte diese Word-Vorlage verwenden: " +
      externalLawyerTemplateLink +
      "\n" +
      "- Verarbeiten der Stellungnahme der Mandantschaft zum Schriftsatz der Gegenseite (wenn vorhanden)\n" +
      "- Wo nötig Beweis antreten bzw. Bestreiten/mit Nichtwissen bestreiten\n" +
      "- Als Abschluss des Schriftsatzes bitte folgende beiden Absätze verwenden: Soweit das Gericht entgegen der " +
      "diesseitigen Auffassung den Vortrag als unzureichend, unschlüssig oder unsubstantiiert ansieht, um den geltend " +
      "gemachten Anspruch als schlüssig zu qualifizieren, oder wenn sonst Bedenken gegen die gefassten Anträge, die " +
      "Schlüssigkeit und die Substantiierung des Vortrages und die hiesige Sichtweise in der Beweislast bestehen, wird " +
      "um einen entsprechenden Hinweis nach § 139 ZPO bzw. eine prozessleitende Verfügung nach § 273 Abs. 2 Nr. 1 ZPO gebeten.\n\n" +
      "Der Unterzeichner weist allein aus Gründen der Vorsorge gegenüber seiner Mandantschaft darauf hin, dass die gerichtliche " +
      "Hinweis- und Aufklärungspflicht nach der neueren Rechtsprechung auch gegenüber der anwaltlich vertretenden Partei " +
      "uneingeschränkt besteht (BGH NJW 2001, 2548; OLG Köln NJW-RR 2001, 1724).",
    reviewRemarks: "",
    assignmentDeadline: { amount: 7, unit: "days" },
    doneDeadline: { amount: 14, unit: "days" },
    maxFee: "75",
    fee: "50",
    isHidden: ({ product }: { product: ProductUnion }) =>
      !product || product.stage === null || product.stage < STAGE_LAWSUIT || product.stage < STAGE_CHECKED,
    getIsUserRequestable: ({ externalLawyerCapacity }: { externalLawyerCapacity: ExternalLawyerCapacity }) =>
      !isWbsLawyer(externalLawyerCapacity.backofficeUser) && externalLawyerCapacity.isAvailable,
    hasEditor: false,
    mainDocumentConfig: {
      headline: "Quadruplik des Beklagten",
      description: "Bitte laden Sie hier die Quadruplik des Klägers als PDF-Datei hoch",
      fileName: "{{YYYYMMDD}}_Quadruplik_{{clientFamilyName}}_aG.pdf",
      fileDescription: "Quadruplik",
    },
    iterableAttachmentsConfig: {
      identifier: "B{{iterator}}",
      headline: "Anlage",
      description:
        " Bitte fügen Sie hier Anlage B{{iterator}} als JPG, PNG oder PDF hoch. Achten Sie darauf, dass in dem Dokument am oberen Bildrand “B{{iterator}}”vermerkt ist.",
      fileName: "{{YYYYMMDD}}_Quadruplik_AnlageB{{iterator}}_{{clientFamilyName}}.pdf",
      fileDescription: "Anlage {{iterator}} Quadruplik",
    },
  },
  {
    type: "fullMandate",
    label: "Vollmandat",
    subject: "",
    description: "",
    definitionOfDone: "",
    reviewRemarks: "",
    assignmentDeadline: { amount: 5, unit: "days" },
    doneDeadline: { amount: 10, unit: "days" },
    maxFee: "125",
    fee: "15",
    isHidden: ({ product }: { product: ProductUnion }) => product.stage === null || product.stage < STAGE_CHECKED,
    getIsUserRequestable: ({ externalLawyerCapacity }: { externalLawyerCapacity: ExternalLawyerCapacity }) =>
      !isWbsLawyer(externalLawyerCapacity.backofficeUser) && externalLawyerCapacity.isAvailable,
    hasEditor: true,
    iterableAttachmentsConfig: genericIterableAttachmentConfig,
  },
  {
    type: "letter",
    label: "Außergerichtliches Schreiben",
    subject: "Außergerichtliches Schreiben",
    description:
      "Im vorliegenden Fall soll ein außergerichtliches Schreiben an die Gegenseite verfasst werden," +
      " das die Zielsetzung der Mandantschaft so gut wie möglich erfüllt. Als Basis dienen die Detailangaben unter" +
      " “Alle Details anzeigen”, die individuelle Beschreibung der Mandantschaft und die hochgeladenen Dokumente. " +
      "Sollten für das Schreiben notwendige Angaben fehlen, sind diese bei der Mandantschaft einzuholen.",
    definitionOfDone:
      "- Text Bevollmächtigung: In vorbezeichneter Angelegenheit zeigen wir an, dass uns Frau Maria Mustermann, " +
      "wohnhaft Musterstraße. 1, 55555 Musterhausen, zur Wahrnehmung und Vertretung ihrer rechtlichen Interessen " +
      "beauftragt hat. Eine auf uns lautende Vollmacht haben wir diesem Schreiben beigefügt.\n" +
      "\n" +
      "- Erstellen des Schriftsatzes über den Editor und Speichern des Entwurfs\n" +
      "\n" +
      "- Korrekte Angaben zur Gegenseite als Empfänger im Briefkopf\n" +
      "\n" +
      "- Anliegen/Standpunkt der Mandantschaft erfasst und verschriftlicht\n" +
      "\n" +
      "- Frist zur Stellungnahme/Erfüllung gesetzt und richtig im Feld “Frist für Gegenseite” gespeichert\n" +
      "\n" +
      "- Besonderheiten oder Informationen zu Rücksprachen mit der Mandantschaft sind per Notizfunktion am Fall vermerkt",
    reviewRemarks: "",
    assignmentDeadline: { amount: 5, unit: "days" },
    doneDeadline: { amount: 8, unit: "days" },
    maxFee: "50",
    fee: "25",
    isHidden: ({ product }: { product: ProductUnion }) =>
      product.stage === null || product.stage > STAGE_ACTION_REQUIRED || product.stage < STAGE_CHECKED,
    getIsUserRequestable: ({ externalLawyerCapacity }: { externalLawyerCapacity: ExternalLawyerCapacity }) =>
      !isWbsLawyer(externalLawyerCapacity.backofficeUser) && externalLawyerCapacity.isAvailable,
    hasEditor: true,
    iterableAttachmentsConfig: genericIterableAttachmentConfig,
    fields: [
      {
        type: "DateField",
        path: `extrajudicialCommunicationDeadline`,
        validators: { validate: { ...dateFieldDefaultFieldBased } },
        headline: "Frist für Gegenseite?",
        description:
          "Wenn Sie der Gegenseite eine Frist gesetzt haben, dann tragen Sie diese bitte ein. Andernfalls lassen Sie das Feld einfach leer.",
      },
    ],
  },
  {
    type: "contractCheck",
    label: "Vertragsprüfung",
    subject: "",
    description: "",
    definitionOfDone: "",
    reviewRemarks: "",
    assignmentDeadline: { amount: 5, unit: "days" },
    doneDeadline: { amount: 10, unit: "days" },
    maxFee: "50",
    fee: "15",
    isHidden: ({ product }: { product: ProductUnion }) => product.stage === null || product.stage < STAGE_CHECKED,
    getIsUserRequestable: ({ externalLawyerCapacity }: { externalLawyerCapacity: ExternalLawyerCapacity }) =>
      !isWbsLawyer(externalLawyerCapacity.backofficeUser) && externalLawyerCapacity.isAvailable,
    hasEditor: true,
    iterableAttachmentsConfig: genericIterableAttachmentConfig,
  },
];

const generalPaidTaskTemplates: PaidTaskTemplate[] = [
  {
    type: "custom",
    label: "Individuelle Aufgabe",
    subject: "",
    description: "",
    definitionOfDone: "",
    reviewRemarks: "",
    assignmentDeadline: { amount: 5, unit: "days" },
    doneDeadline: { amount: 10, unit: "days" },
    maxFee: "50",
    fee: "15",
    isHidden: ({ product, productStages }: { product: ProductUnion; productStages: ProductUnion }) =>
      product.stage === null || product.stage < productStages.STAGE_CHECKED,
    getIsUserRequestable: ({ externalLawyerCapacity }: { externalLawyerCapacity: ExternalLawyerCapacity }) =>
      !isWbsLawyer(externalLawyerCapacity.backofficeUser) && externalLawyerCapacity.isAvailable,
    hasEditor: true,
    iterableAttachmentsConfig: genericIterableAttachmentConfig,
  },
];

export const getTemplates = (product: ProductUnion) => {
  let templates = [...generalPaidTaskTemplates];
  switch (product.productClassName) {
    case "eventContract":
    case "membershipContract":
    case "otherContract":
    case "providerContract":
    case "purchaseContract":
    case "serviceContract":
    case "subscriptionContract":
    case "travelContract":
    case "vehicleContract":
    case "trafficAccident":
    case "generalCivilLaw":
    case "employmentContract":
      templates = [...templates, ...litigationCaseTemplates];
      break;
    case "civilServiceLaw":
      templates = [
        ...templates,
        ...litigationCaseTemplates,
        {
          type: "objection",
          label: "Widerspruch gegen Bescheid",
          subject: "Widerspruch verfassen",
          description:
            "Im vorliegenden Fall soll gegen den Bescheid der Behörde fristgerecht Widerspruch eingelegt und dieser rechtlich sowie tatsächlich begründet werden. Ziel ist die Abhilfe durch die Behörde (Aufhebung oder Änderung des Bescheids) zugunsten der Mandantschaft. Als Basis dienen die Detailangaben, die hochgeladenen Dokumente (insb. der angefochtene Bescheid) und die individuelle Fallbeschreibung. Prüfen Sie insbesondere die Einhaltung der Widerspruchsfrist gemäß Rechtsbehelfsbelehrung.",
          definitionOfDone:
            "- Im vorliegenden Fall soll ein Widerspruch an die Gegenseite verfasst werden Als Basis dienen die Detailangaben unter “Alle Details anzeigen”, die individuelle Beschreibung der Mandantschaft und die hochgeladenen Dokumente. Sollten für das Schreiben notwendige Angaben fehlen, sind diese bei der Mandantschaft einzuholen.\n" +
            "\n" +
            "- Text Bevollmächtigung: In vorbezeichneter Angelegenheit zeigen wir an, dass uns Frau Maria Mustermann, wohnhaft Musterstraße. 1, 55555 Musterhausen, zur Wahrnehmung und Vertretung ihrer rechtlichen Interessen beauftragt hat. Eine auf uns lautende Vollmacht haben wir diesem Schreiben beigefügt.\n" +
            "\n" +
            "- Korrekte Angaben zur Gegenseite als Empfänger im Briefkopf\n" +
            "\n" +
            "- Anliegen/Standpunkt der Mandantschaft erfasst und verschriftlicht\n" +
            "\n" +
            "- Frist zur Stellungnahme/Erfüllung gesetzt und richtig im Feld “Frist für Gegenseite” gespeichert\n" +
            "\n" +
            "- Besonderheiten oder Informationen zu Rücksprachen mit der Mandantschaft sind per Notizfunktion am Fall vermerkt",
          reviewRemarks: "",
          assignmentDeadline: { amount: 4, unit: "days" },
          doneDeadline: { amount: 7, unit: "days" },
          maxFee: "100",
          fee: "40",
          isHidden: ({ product }: { product: ProductUnion }) => product.stage !== STAGE_ADMINISTRATIVE_DECISION_ISSUED,
          getIsUserRequestable: ({ externalLawyerCapacity }: { externalLawyerCapacity: ExternalLawyerCapacity }) =>
            externalLawyerCapacity.isAvailable,
          hasEditor: false,
          mainDocumentConfig: {
            headline: "Widerspruch gegen Bescheid",
            description: "Bitte laden Sie hier den Widerspruch gegen den Bescheid als PDF-Datei hoch",
            fileName: "{{YYYYMMDD}}_Widerspruch_gegen_Bescheid_{{clientFamilyName}}.pdf",
            fileDescription: "Widerspruch gegen Bescheid",
          },
          iterableAttachmentsConfig: genericIterableAttachmentConfig,
        },
        {
          type: "administrativeCorrespondence",
          label: "Schreiben an Behörde",
          subject: "Schreiben an Behörde verfassen",
          description:
            "Im vorliegenden Fall soll ein substantiiertes Schreiben an die zuständige Behörde oder Dienststelle verfasst werden. Es handelt sich hierbei noch nicht um einen förmlichen Rechtsbehelf (Widerspruch), sondern um das vor- oder außergerichtliche Verwaltungsverfahren.\nZiel der Maßnahme: [Je nach Fall: Erstantrag stellen / Stellungnahme im Anhörungsverfahren abgeben / Sachstandsanfrage bei Untätigkeit]. Der Sachverhalt ist basierend auf den Angaben der Mandantschaft umfassend und rechtlich fundiert darzulegen. Bei Ermessensentscheidungen sind die für die Mandantschaft sprechenden Aspekte (Fürsorgepflicht, persönliche Härte) hervorzuheben.",
          definitionOfDone:
            "- Im vorliegenden Fall soll ein Schreiben an die Gegenseite verfasst werden Als Basis dienen die Detailangaben unter “Alle Details anzeigen”, die individuelle Beschreibung der Mandantschaft und die hochgeladenen Dokumente. Sollten für das Schreiben notwendige Angaben fehlen, sind diese bei der Mandantschaft einzuholen.\n" +
            "\n" +
            "- Text Bevollmächtigung: In vorbezeichneter Angelegenheit zeigen wir an, dass uns Frau Maria Mustermann, wohnhaft Musterstraße. 1, 55555 Musterhausen, zur Wahrnehmung und Vertretung ihrer rechtlichen Interessen beauftragt hat. Eine auf uns lautende Vollmacht haben wir diesem Schreiben beigefügt.\n" +
            "\n" +
            "- Korrekte Angaben zur Gegenseite als Empfänger im Briefkopf\n" +
            "\n" +
            "- Anliegen/Standpunkt der Mandantschaft erfasst und verschriftlicht\n" +
            "\n" +
            "- Frist zur Stellungnahme/Erfüllung gesetzt und richtig im Feld “Frist für Gegenseite” gespeichert\n" +
            "\n" +
            "- Besonderheiten oder Informationen zu Rücksprachen mit der Mandantschaft sind per Notizfunktion am Fall vermerkt",
          reviewRemarks: "",
          assignmentDeadline: { amount: 4, unit: "days" },
          doneDeadline: { amount: 7, unit: "days" },
          maxFee: "100",
          fee: "40",
          isHidden: ({ product }: { product: ProductUnion }) =>
            product.stage < STAGE_CHECKED || product.stage > STAGE_ACTION_REQUIRED,
          getIsUserRequestable: ({ externalLawyerCapacity }: { externalLawyerCapacity: ExternalLawyerCapacity }) =>
            externalLawyerCapacity.isAvailable,
          hasEditor: false,
          mainDocumentConfig: {
            headline: "Schreiben an Behörde",
            description: "Bitte laden Sie hier das Schreiben an die Behörde als PDF-Datei hoch",
            fileName: "{{YYYYMMDD}}_Schreiben_an_Behoerde_{{clientFamilyName}}.pdf",
            fileDescription: "Schreiben an Behörde",
          },
          iterableAttachmentsConfig: genericIterableAttachmentConfig,
        },
        {
          type: "expeditedApplicationAdministrativeLaw",
          label: "Eilantrag",
          subject: "Eilantrag verfassen",
          description:
            "Im vorliegenden Fall soll ein Eilantrag an das zuständige Verwaltungsgericht erstellt werden." +
            " Ziel ist eine gerichtliche Eilentscheidung um die Interessen der Mandantschaft vorläufig zu sichern. Als Basis dienen die Detailangaben, die hochgeladenen Dokumente (insb. der angefochtene Bescheid/die Verfügung) und die individuelle Fallbeschreibung. Prüfen Sie insbesondere die Zuständigkeit des Gerichts sowie die Eilbedürftigkeit (Anordnungsgrund)." +
            " Bei etwaigen Fragen zur Sache selber kann die Mandantschaft angerufen werden. Der Anruf ist per Notiz zu dokumentieren.",
          definitionOfDone:
            "Gerichtsstand ermitteln:\n" +
            " - Der Gerichtsstand ist anhand der Falldaten zu bestimmen und unten im Feld 'PLZ Gerichtsstand' einzutragen\n" +
            "\n" +
            "\n" +
            "\n" +
            "Folgende Anträge sind zu stellen:\n" +
            " - Antrag auf Anordnung bzw. Wiederherstellung der aufschiebenden Wirkung (§ 80 Abs. 5 VwGO) ODER auf Erlass einer einstweiligen Anordnung (§ 123 VwGO) je nach Fallkonstellation (siehe Anordnungsanspruch/-grund).\n" +
            " - Verfahrensanträge (z.B. Kostenentscheidung) sind aus der Vorlage zu übernehmen. " +
            "Glaubhaftmachung der Eilbedürftigkeit (Anordnungsgrund) ist zwingend erforderlich.\n" +
            "\n" +
            "Formelle Anforderungen:\n" +
            " - Erstellen des Schriftsatzes als PDF Datei\n" +
            " - Das PDF soll bei Ihnen lokal erzeugt und dann unten hochgeladen werden.\n" +
            " - Anlagen sollen jeweils einzeln erstellt und durchnummeriert werden (A1, A2, etc.). „Anlage A1“ etc. muss oben " +
            "rechts auf Dokument stehen.\n" +
            " - Das Eilantrags-pdf und alle Anlagen als weitere Einzel pdfs sind unten unter 'Bearbeitung' hochzuladen\n" +
            " - Besonderheiten oder Informationen zu Rücksprachen mit der Mandantschaft sind per Notizfunktion am Fall vermerkt.\n" +
            " - Sachfragen bitte an Mandantschaft direkt stellen; juristische bzw. Verfahrensfragen gerne an jeweiligen im Fall " +
            "hinterlegten RA. Verbesserungsvorschläge sind jederzeit gerne willkommen!",
          reviewRemarks: "",
          assignmentDeadline: { amount: 2, unit: "days" },
          doneDeadline: { amount: 2, unit: "days" },
          maxFee: "100",
          fee: "40",
          isHidden: ({ product }: { product: ProductUnion }) =>
            !product ||
            product.stage === null ||
            product.stage < STAGE_PREPARE_LAWSUIT ||
            product.stage > STAGE_LAWSUIT,
          getIsUserRequestable: ({ externalLawyerCapacity }: { externalLawyerCapacity: ExternalLawyerCapacity }) =>
            !isWbsLawyer(externalLawyerCapacity.backofficeUser) && externalLawyerCapacity.isAvailable,
          hasEditor: false,
          mainDocumentConfig: {
            headline: "Eilantrag",
            description: "Bitte laden Sie hier den Eilantrag als PDF-Datei hoch",
            fileName: "{{YYYYMMDD}}Eilantrag{{clientFamilyName}}.pdf",
            fileDescription: "Eilantrag",
          },
          iterableAttachmentsConfig: {
            identifier: "A{{iterator}}",
            headline: "Anlage",
            description:
              "Bitte laden Sie hier Anlage A{{iterator}} hoch. Achten Sie darauf, dass in dem Dokument am oberen Bildrand 'A{{iterator}}' vermerkt ist.",
            fileName: "{{YYYYMMDD}}_EilantragAnlageA{{iterator}}{{clientFamilyName}}.pdf",
            fileDescription: "Anlage A{{iterator}} Eilantrag",
          },
          fields: [
            {
              type: "TextField",
              path: `courtPostalCode`,
              validators: requiredFieldDefaultFieldBased,
              headline: "Postleitzahl des Gerichtsstands",
              description:
                "Bitte tragen Sie hier die Postleitzahl ein, die Sie für den Gerichtsstand ermittelt haben. Wichtig: Bitte NICHT die Postleitzahl des Gerichtes eintragen, sondern die des Gerichtsstands.",
            },
            {
              type: "NumberField",
              path: `valueInDisputeJudicialPreliminary`,
              validators: requiredFieldDefaultFieldBased,
              headline: "Vorläufiger Streitwert",
              description: "Bitte tragen Sie hier den vorläufigen Streitwert ein, den Sie im Eilantrag vermerkt haben.",
              fieldType: "money",
              numberType: "float",
            },
          ],
        },
      ];
      break;
    case "rentalContract":
      templates = [
        ...templates,
        ...litigationCaseTemplates,
        {
          type: "utilityBillReview",
          label: "Nebenkostenprüfung",
          subject: "Nebenkostenprüfung",
          description:
            "Bitte die Nebenkostenabrechnung prüfen und je nach Erfolgsaussichten eine Stellungnahme an der Mandantschaft oder die Gegenseite formulieren",
          definitionOfDone:
            "- Text Bevollmächtigung: In vorbezeichneter Angelegenheit zeigen wir an, dass uns Frau Maria Mustermann, " +
            "wohnhaft Musterstraße. 1, 55555 Musterhausen, zur Wahrnehmung und Vertretung ihrer rechtlichen Interessen " +
            "beauftragt hat. Eine auf uns lautende Vollmacht haben wir diesem Schreiben beigefügt.\n" +
            "\n" +
            "- Erstellen des Schriftsatzes über den Editor und Speichern des Entwurfs\n" +
            "\n" +
            "- Korrekte Angaben zur Gegenseite als Empfänger im Briefkopf\n" +
            "\n" +
            "- Anliegen/Standpunkt der Mandantschaft erfasst und verschriftlicht\n" +
            "\n" +
            "- Frist zur Stellungnahme/Erfüllung gesetzt und richtig im Feld “Frist für Gegenseite” gespeichert\n" +
            "\n" +
            "- Besonderheiten oder Informationen zu Rücksprachen mit der Mandantschaft sind per Notizfunktion am Fall vermerkt",
          reviewRemarks: "",
          assignmentDeadline: { amount: 5, unit: "days" },
          doneDeadline: { amount: 8, unit: "days" },
          maxFee: "35",
          fee: "35",
          isHidden: ({ product }: { product: ProductUnion }) =>
            !product || product.stage === null || product.stage < STAGE_CHECKED,
          getIsUserRequestable: ({ externalLawyerCapacity }: { externalLawyerCapacity: ExternalLawyerCapacity }) =>
            !isWbsLawyer(externalLawyerCapacity.backofficeUser) && externalLawyerCapacity.isAvailable,
          hasEditor: true,
          iterableAttachmentsConfig: genericIterableAttachmentConfig,
        },
      ];
      break;
    case "dataLeakContract":
      templates = [
        ...templates,
        ...litigationCaseTemplates,
        {
          type: "dataLeakCounterplea",
          label: "Schriftsatz Gerichtsverfahren Illegale Datenweitergabe",
          subject: "Schriftsatz Gerichtsverfahren Illegale Datenweitergabe",
          description: "Bitte Antwortschreiben auf die neueste gerichtliche Korrespondenz am Fall erstellen",
          definitionOfDone:
            "- Vorlage für den richtigen Anwalt auswählen\n(https://drive.google.com/drive/u/0/folders/1dWyXJXC-07n1IV-d2G-pZs2N1_x5zHHa)\n\n" +
            "- Erstellen des Schriftsatzes und Upload als PDF\n\n" +
            "- Bitte folgende Daten für das Schreiben benutzen:\n\n" +
            `Aktuelles Datum\n` +
            `Zuständiger Anwalt: <%= product.responsibleLawyer.fullName %>\n` +
            `Empfänger Gericht: <%= translate(product, "responsibleCourt.name") %>,` +
            ` <%=translate(product, "addressWithPostOfficeBoxFallback.responsibleCourt.address") %>\n` +
            `Aktenzeichen Gericht: <%=translate(product, "courtReference") %>\n` +
            `Unser Aktenzeichen: <%= translate(product, "reference") %>\n` +
            `Mandantschaft: <%= translate(product, "client.gender") %>` +
            ` <%= translate(product, "client.givenName") %> <%= translate(product, "client.familyName") %>, <%= translate(product, "client.residenceAddress.streetAddress") %>,` +
            ` <%= translate(product, "client.residenceAddress.postalCode") %> <%= translate(product, "client.residenceAddress.addressLocality") %>\n` +
            `Gegenseite: <%= translate(product, "contractualPartnerLegalEntity.organization.legalName") %>, <%= translate(product, "addressWithPostOfficeBoxFallback.contractualPartnerLegalEntity.address") %>\n\n` +
            "- Besonderheiten oder Informationen zu Rücksprachen mit der Mandantschaft oder den anzuhängenden Dateien sind per Notizfunktion am Fall vermerkt",
          reviewRemarks: "",
          assignmentDeadline: { amount: 3, unit: "days" },
          doneDeadline: { amount: 10, unit: "days" },
          maxFee: "0",
          fee: "0",
          isHidden: ({ product }: { product: ProductUnion }) =>
            !product || product.stage === null || product.stage < STAGE_LAWSUIT,
          getIsUserRequestable: ({ externalLawyerCapacity }: { externalLawyerCapacity: ExternalLawyerCapacity }) =>
            isWbsLawyer(externalLawyerCapacity.backofficeUser),
          hasEditor: false,
          mainDocumentConfig: {
            headline: "Schriftsatz Gerichtsverfahren",
            description: "Bitte laden Sie hier den Schriftsatz als PDF-Datei hoch",
            fileName: "{{YYYYMMDD}}_Schriftsatz_{{clientFamilyName}}_aG.pdf",
            fileDescription: "Schriftsatz gerichtliches Verfahren",
          },
          iterableAttachmentsConfig: {
            identifier: "K{{iterator}}",
            headline: "Anlage",
            description:
              "Bitte laden Sie hier Anlage K{{iterator}} hoch. Achten Sie darauf, dass in dem Dokument am oberen Bildrand “K{{iterator}}”vermerkt ist.",
            fileName: "{{YYYYMMDD}}_Schriftsatz_AnlageK{{iterator}}_{{clientFamilyName}}.pdf",
            fileDescription: "Anlage K{{iterator}} Schriftsatz gerichtliches Verfahren",
          },
        },
      ];
      break;
    default:
      break;
  }
  return templates;
};

export const paidTaskTemplateFields = {
  basic: ["label", "subject", "description", "reviewRemarks", "fee"],
  date: ["assignmentDeadline", "doneDeadline"],
  placeholderText: ["definitionOfDone"],
};

export const getSubmissionCardEnabled = (paidTaskStatus: PaidTask["status"], isExternalUser: boolean) => {
  switch (true) {
    case isExternalUser:
      return paidTaskStatus === PAID_TASK_STATUS_ASSIGNED;
    case !isExternalUser:
      return paidTaskStatus === PAID_TASK_STATUS_IN_REVIEW;
    default:
      return false;
  }
};

export const getApprovalCardEnabled = (paidTaskStatus: PaidTask["status"], currentUser: BackofficeUser) => {
  switch (true) {
    case userHasRole(currentUser, Roles.external):
      return paidTaskStatus >= PAID_TASK_STATUS_ASSIGNED;
    case userHasOneOfTheseRoles(currentUser, [
      Roles.accountmanager,
      Roles.lawyer,
      Roles.traineeLawyer,
      Roles.admin,
      Roles.developer,
    ]):
      return paidTaskStatus >= PAID_TASK_STATUS_IN_REVIEW;
    default:
      return false;
  }
};

export const getInitialValuesForPaidTask = (
  paidTaskConfig: PaidTaskTemplate,
  product: ProductUnion,
  paidTask: PaidTask
) => {
  let initialValues: UseForm["values"] = {};

  const formData = paidTask.formData;

  if (paidTaskConfig.hasEditor) {
    initialValues.sigantureType = formData?.sigantureType || "internal";
    initialValues.recipientType = formData?.recipientType || "custom";
    initialValues.address = {
      name: formData?.address?.name || "",
      department: formData?.address?.department || "",
      subDepartment: formData?.address?.subDepartment || "",
      streetAddress: formData?.address?.streetAddress || "",
      postalCode: formData?.address?.postalCode || "",
      addressLocality: formData?.address?.addressLocality || "",
    };
    initialValues.senderMedium = formData?.senderMedium || "none";
    initialValues.externalReference = formData?.externalReference || "";
    initialValues.externalReferenceSelect = formData?.externalReferenceSelect || "free";
    initialValues.filename = formData?.filename || dayjs().format("YYYYMMDD") + "_Aussergerichtliches_Schreiben";
    initialValues.description = formData?.description;
    initialValues.showInGallery = formData?.showInGallery || "false";
    initialValues.sendEmailToCustomer = formData?.sendEmailToCustomer || "false";
    initialValues.selectedMediaObjects = formData?.selectedMediaObjects ? [...formData.selectedMediaObjects] : [];
  }

  _.forEach(paidTaskConfig.fields, (field) => {
    const path = _.isFunction(field.path) ? field.path({ product }) : field.path;
    let value = _.get(formData, "temporaryField." + path, null);
    if (value && field.type === "DateField") {
      value = dayjs(value);
    }
    initialValues["temporaryField." + path] = value;
  });

  return initialValues;
};

export const paidTaskListFilters = {
  assigneeView: {
    assigned: {
      filter: (paidTask: PaidTask) => {
        return paidTask.status === PAID_TASK_STATUS_ASSIGNED;
      },
      sorter: (paidTask: PaidTask) => {
        return paidTask.doneDeadline;
      },
    },
    inReview: {
      filter: (paidTask: PaidTask) => {
        return paidTask.status === PAID_TASK_STATUS_IN_REVIEW;
      },
      sorter: (paidTask: PaidTask) => {
        return paidTask.doneDeadline;
      },
    },
    approved: {
      filter: (paidTask: PaidTask) => {
        return paidTask.status === PAID_TASK_STATUS_APPROVED;
      },
      sorter: (paidTask: PaidTask) => {
        return paidTask.doneDeadline;
      },
    },
  },
  internalView: {
    assignmentDeadline: {
      filter: (paidTask: PaidTask) => {
        return paidTask.status === PAID_TASK_STATUS_CREATED && dayjs().isAfter(paidTask.assignmentDeadline, "day");
      },
      sorter: (paidTask: PaidTask) => {
        return paidTask.assignmentDeadline;
      },
    },
    doneDeadline: {
      filter: (paidTask: PaidTask) => {
        paidTask.status === PAID_TASK_STATUS_ASSIGNED && dayjs().isAfter(paidTask.doneDeadline, "day");
      },
      sorter: (paidTask: PaidTask) => {
        return paidTask.doneDeadline;
      },
    },
    open: {
      filter: (paidTask: PaidTask) => {
        return paidTask.status === PAID_TASK_STATUS_CREATED;
      },
      sorter: (paidTask: PaidTask) => {
        return paidTask.assignmentDeadline;
      },
    },
    requested: {
      filter: (paidTask: PaidTask) => {
        return paidTask.status === PAID_TASK_STATUS_REQUESTED;
      },
      sorter: (paidTask: PaidTask) => {
        return paidTask.assignmentDeadline;
      },
    },
    assigned: {
      filter: (paidTask: PaidTask) => {
        return paidTask.status === PAID_TASK_STATUS_ASSIGNED;
      },
      sorter: (paidTask: PaidTask) => {
        return paidTask.doneDeadline;
      },
    },
    inReview: {
      filter: (paidTask: PaidTask) => {
        return paidTask.status === PAID_TASK_STATUS_IN_REVIEW;
      },
      sorter: (paidTask: PaidTask) => {
        return paidTask.doneDeadline;
      },
    },
    approved: {
      filter: (paidTask: PaidTask) => {
        return paidTask.status === PAID_TASK_STATUS_APPROVED;
      },
      sorter: (paidTask: PaidTask) => {
        return paidTask.approvedDate;
      },
    },
  },
  assignableView: {
    open: {
      filter: (paidTask: PaidTask) => {
        return paidTask.status === PAID_TASK_STATUS_CREATED;
      },
      sorter: (paidTask: PaidTask) => {
        return paidTask.assignmentDeadline;
      },
    },
    requested: {
      filter: (paidTask: PaidTask) => {
        return paidTask.status === PAID_TASK_STATUS_REQUESTED;
      },
      sorter: (paidTask: PaidTask) => {
        return paidTask.assignmentDeadline;
      },
    },
  },
};

export const updateFeeValidators = async (
  registerValidators: UseForm["registerValidators"],
  paidTaskType: PaidTask["type"],
  product: ProductUnion
) => {
  if (!paidTaskType) {
    return;
  }
  const typeConfig = _.find(getTemplates(product), (template) => template.type === paidTaskType);
  if (!typeConfig) {
    return;
  }

  const convertedMaxFee = parseInt(typeConfig.maxFee);
  const maxFee =
    typeConfig.type !== "utilityBillReview"
      ? Math.min(convertedMaxFee, (await fetchBudget()).remainingBudget)
      : convertedMaxFee;

  registerValidators("fee", [
    ...requiredFieldDefaultLegacy,
    ...intFieldDefault,
    {
      validator: maxNumber,
      params: [maxFee],
      message: `Der Betrag darf maximal ${maxFee}€ betragen`,
    },
  ]);
};

export const isWbsLawyer = (lawyer: BackofficeUser) => {
  return [63, 64, 65].includes(lawyer?.id);
};

export const fetchBudget = async () => {
  return await ApiClient.get("/paid_task_budget");
};
